import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import {
  PAYMENT_STATUSES,
  PAYMENT_STATUS_LABELS,
  ORDER_STATUSES
} from '../firebase/schema';
import {
  markOrderPaidOffline,
  syncOrderPaymentIfInvoiceable,
  getPaymentDueDate,
  isPast30DaysOverdue
} from '../utils/orderPayment';
import { useAuth } from '../context/AuthContext';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';
import { formatDate } from '../utils/dateFormatter';

const ACCENT = 'var(--al-primary)';
const PAGE_BG = 'var(--al-bg)';
const CARD_BG = 'var(--al-surface)';

const Page = styled.div`
  min-height: 100vh;
  background: ${PAGE_BG};
  color: #fff;
  padding: 88px 24px 48px;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.header`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 2rem;
  color: ${ACCENT};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Subtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.95rem;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 16px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  text-decoration: none;

  &:hover {
    color: ${ACCENT};
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${CARD_BG};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 16px 20px;
`;

const StatLabel = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${ACCENT};
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: 8px 16px;
  border: 1px solid ${(p) => (p.$active ? ACCENT : 'rgba(255,255,255,0.15)')};
  border-radius: 6px;
  background: ${(p) => (p.$active ? 'rgba(var(--al-primary-rgb), 0.12)' : 'transparent')};
  color: ${(p) => (p.$active ? ACCENT : 'rgba(255,255,255,0.8)')};
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  background: ${CARD_BG};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  vertical-align: middle;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(p) => p.$bg || 'rgba(255,255,255,0.1)'};
  color: ${(p) => p.$color || '#fff'};
`;

const ActionBtn = styled.button`
  padding: 6px 10px;
  margin-right: 6px;
  border: none;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  background: ${ACCENT};
  color: var(--al-bg);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Empty = styled.p`
  padding: 32px;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
`;

const TABS = {
  OUTSTANDING: 'outstanding',
  PAID: 'paid',
  ALL: 'all'
};

function paymentBadgeStyle(status) {
  switch (status) {
    case PAYMENT_STATUSES.AWAITING_PAYMENT:
      return { bg: 'rgba(255, 165, 0, 0.2)', color: '#ffa500' };
    case PAYMENT_STATUSES.PENDING:
      return { bg: 'rgba(255, 215, 0, 0.15)', color: '#ffd700' };
    case PAYMENT_STATUSES.PAID:
    case PAYMENT_STATUSES.PAID_OFFLINE:
      return { bg: 'rgba(var(--al-primary-rgb), 0.15)', color: ACCENT };
    case PAYMENT_STATUSES.WAIVED:
      return { bg: 'rgba(255,255,255,0.1)', color: '#aaa' };
    default:
      return { bg: 'rgba(255,255,255,0.08)', color: '#888' };
  }
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPaidAt(paidAt) {
  if (!paidAt) return '—';
  try {
    const d = paidAt.toDate ? paidAt.toDate() : new Date(paidAt);
    return formatDate(d);
  } catch {
    return '—';
  }
}

function OwnerPayments() {
  const { user } = useAuth();
  const { facilityId } = useFacility();
  const [tab, setTab] = useState(TABS.OUTSTANDING);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const statuses = [ORDER_STATUSES.READY_TO_INVOICE, ORDER_STATUSES.COMPLETED];
      const invoiceableSnap = await getDocs(
        query(collection(db, 'orders'), where('status', 'in', statuses))
      );

      const initial = invoiceableSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      await Promise.all(
        initial
          .filter(
            (o) =>
              o.status === ORDER_STATUSES.READY_TO_INVOICE &&
              (!o.payment?.status || o.payment.status === PAYMENT_STATUSES.NOT_DUE)
          )
          .map((o) => syncOrderPaymentIfInvoiceable(db, o.id))
      );

      const refreshedSnap = await getDocs(
        query(collection(db, 'orders'), where('status', 'in', statuses))
      );
      const refreshed = filterByFacility(
        refreshedSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        facilityId
      );
      refreshed.sort((a, b) => (Number(b.poNumber) || 0) - (Number(a.poNumber) || 0));
      setOrders(refreshed);
    } catch (error) {
      console.error('OwnerPayments load:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const filtered = useMemo(() => {
    if (tab === TABS.OUTSTANDING) {
      return orders.filter((o) =>
        [PAYMENT_STATUSES.AWAITING_PAYMENT, PAYMENT_STATUSES.PENDING].includes(o.payment?.status)
      );
    }
    if (tab === TABS.PAID) {
      return orders.filter((o) =>
        [PAYMENT_STATUSES.PAID, PAYMENT_STATUSES.PAID_OFFLINE].includes(o.payment?.status)
      );
    }
    return orders.filter((o) => o.payment?.status);
  }, [orders, tab]);

  const stats = useMemo(() => {
    const outstanding = orders.filter((o) =>
      [PAYMENT_STATUSES.AWAITING_PAYMENT, PAYMENT_STATUSES.PENDING].includes(o.payment?.status)
    );
    const outstandingTotal = outstanding.reduce((sum, o) => sum + (Number(o.payment?.amountDue) || 0), 0);
    const paid = orders.filter((o) =>
      [PAYMENT_STATUSES.PAID, PAYMENT_STATUSES.PAID_OFFLINE].includes(o.payment?.status)
    );
    const paidTotal = paid.reduce((sum, o) => sum + (Number(o.payment?.amountPaid) || 0), 0);
    return {
      outstandingCount: outstanding.length,
      outstandingTotal,
      paidCount: paid.length,
      paidTotal
    };
  }, [orders]);

  const handleMarkPaidOffline = async (order) => {
    const note = window.prompt(`Mark invoice #${order.poNumber || order.id} as paid offline?\n\nOptional note:`);
    if (note === null) return;

    setActingId(order.id);
    try {
      await markOrderPaidOffline(db, order.id, {
        staffEmail: user?.email || 'staff',
        note
      });
      await loadOrders();
    } catch (error) {
      alert(error.message || 'Could not mark paid.');
    } finally {
      setActingId(null);
    }
  };

  return (
    <Page>
      <Inner>
        <BackLink to="/owner">← Owner dashboard</BackLink>
        <Header>
          <Title>Payments</Title>
          <Subtitle>Outstanding and paid invoices. Portal payments (Stripe) update here automatically.</Subtitle>
        </Header>

        <StatGrid>
          <StatCard>
            <StatLabel>Outstanding</StatLabel>
            <StatValue>{formatMoney(stats.outstandingTotal)}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Invoices awaiting payment</StatLabel>
            <StatValue>{stats.outstandingCount}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Paid (on record)</StatLabel>
            <StatValue>{formatMoney(stats.paidTotal)}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Paid invoice count</StatLabel>
            <StatValue>{stats.paidCount}</StatValue>
          </StatCard>
        </StatGrid>

        <TabRow>
          <Tab $active={tab === TABS.OUTSTANDING} type="button" onClick={() => setTab(TABS.OUTSTANDING)}>
            Outstanding
          </Tab>
          <Tab $active={tab === TABS.PAID} type="button" onClick={() => setTab(TABS.PAID)}>
            Paid
          </Tab>
          <Tab $active={tab === TABS.ALL} type="button" onClick={() => setTab(TABS.ALL)}>
            All with payment
          </Tab>
        </TabRow>

        <TableWrap>
          {loading ? (
            <Empty>Loading…</Empty>
          ) : filtered.length === 0 ? (
            <Empty>No invoices in this view.</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Invoice #</Th>
                  <Th>Customer</Th>
                  <Th>Order status</Th>
                  <Th>Payment</Th>
                  <Th>Due</Th>
                  <Th>Amount</Th>
                  <Th>Paid at</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((order) => {
                  const ps = order.payment?.status;
                  const badge = paymentBadgeStyle(ps);
                  const isOutstanding = [PAYMENT_STATUSES.AWAITING_PAYMENT, PAYMENT_STATUSES.PENDING].includes(ps);
                  const amount = isOutstanding
                    ? order.payment?.amountDue
                    : order.payment?.amountPaid ?? order.payment?.amountDue;

                  return (
                    <tr key={order.id}>
                      <Td>{order.poNumber || order.orderNumber || '—'}</Td>
                      <Td>{order.customerName || '—'}</Td>
                      <Td>{order.status || '—'}</Td>
                      <Td>
                        <Badge $bg={badge.bg} $color={badge.color}>
                          {PAYMENT_STATUS_LABELS[ps] || ps || '—'}
                        </Badge>
                        {isPast30DaysOverdue(order) && (
                          <Badge $bg="rgba(255, 80, 80, 0.2)" $color="#ff6b6b" style={{ marginLeft: 6 }}>
                            Past 30
                          </Badge>
                        )}
                      </Td>
                      <Td>{formatPaidAt(getPaymentDueDate(order))}</Td>
                      <Td>{formatMoney(amount)}</Td>
                      <Td>{formatPaidAt(order.payment?.paidAt)}</Td>
                      <Td>
                        <ActionBtn as={Link} to="/invoicing" style={{ textDecoration: 'none', display: 'inline-block' }}>
                          Invoice
                        </ActionBtn>
                        {isOutstanding && (
                          <ActionBtn
                            type="button"
                            disabled={actingId === order.id}
                            onClick={() => handleMarkPaidOffline(order)}
                          >
                            Paid offline
                          </ActionBtn>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </TableWrap>
      </Inner>
    </Page>
  );
}

export default OwnerPayments;
